import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Save, Settings2, Mic, Info as InfoIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useEnvironment } from '../EnvironmentProvider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '../designSystem';

const SettingsDialog = ({ open, onOpenChange, isDarkMode, setIsDarkMode }) => {
  const { API_BASE_URL, userId } = useEnvironment();
  const [error, setError] = useState(null);
  const [autoDelete, setAutoDelete] = useState(true);
  const [transcriptionService, setTranscriptionService] = useState('azure');
  const [whisperModel, setWhisperModel] = useState('whisper-1');
  const [postProcessing, setPostProcessing] = useState(false);
  const [temperature, setTemperature] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      if (!open || !userId) return;
      
      try {
        setError(null);
        // Remove the duplicate /api/ in the URL
        const response = await fetch(`${API_BASE_URL}/config`, {
          headers: {
            'X-User-Id': userId,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Kunne ikke hente innstillinger');
        }
        const data = await response.json();
        setAutoDelete(data.auto_delete ?? true);
        setTranscriptionService(data.transcription_service || 'azure');
        
        // Set Whisper settings if available
        if (data.whisper) {
          setWhisperModel(data.whisper.model || 'whisper-1');
          setPostProcessing(data.whisper.post_processing || false);
          setTemperature(data.whisper.temperature || 0);
        }
        
        if (typeof data.dark_mode !== 'undefined') {
          setIsDarkMode(data.dark_mode);
          localStorage.setItem('darkMode', data.dark_mode);
        }
      } catch (error) {
        console.error('Error fetching config:', error);
        setError('Kunne ikke hente innstillinger');
      }
    };

    fetchConfig();
  }, [open, API_BASE_URL, userId, setIsDarkMode]);

  const handleDarkModeChange = (checked) => {
    setIsDarkMode(checked);
    localStorage.setItem('darkMode', checked);
  };

  const handleTranscriptionServiceChange = (value) => {
    setTranscriptionService(value);
  };

  const handleSave = async () => {
    try {
      setError(null);
      setIsSaving(true);

      // Hent først nåværende konfigurasjon
      let currentConfig = {};
      try {
        // Remove the duplicate /api/ in the URL
        const configResponse = await fetch(`${API_BASE_URL}/config`, {
          headers: {
            'X-User-Id': userId,
            'Content-Type': 'application/json'
          }
        });
        
        if (configResponse.ok) {
          currentConfig = await configResponse.json();
        }
      } catch (error) {
        console.error('Kunne ikke hente nåværende konfigurasjon:', error);
        // Fortsett likevel, vi vil bare oppdatere spesifikke felt
      }
      
      // Update Whisper settings within config
      if (!currentConfig.whisper) {
        currentConfig.whisper = {};
      }
      
      currentConfig.whisper.model = whisperModel;
      currentConfig.whisper.post_processing = postProcessing;
      currentConfig.whisper.temperature = parseFloat(temperature);
      
      // Oppdater bare de spesifikke feltene
      const updatedConfig = {
        ...currentConfig,
        auto_delete: autoDelete,
        dark_mode: isDarkMode,
        transcription_service: transcriptionService
      };

      // Remove the duplicate /api/ in the URL
      const response = await fetch(`${API_BASE_URL}/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId
        },
        body: JSON.stringify(updatedConfig),
      });

      if (!response.ok) {
        throw new Error('Kunne ikke lagre innstillinger');
      }
      
      // Update the active transcription service
      if (transcriptionService) {
        try {
          // Remove the duplicate /api/ in the URL
          const serviceResponse = await fetch(`${API_BASE_URL}/set-transcription-service`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Id': userId
            },
            body: JSON.stringify({ service_type: transcriptionService }),
          });
          
          if (!serviceResponse.ok) {
            console.error('Failed to update transcription service but continuing');
          }
        } catch (serviceError) {
          console.error('Error updating transcription service:', serviceError);
        }
      }

      // Oppdater dark mode i localStorage og DOM
      localStorage.setItem('darkMode', isDarkMode);
      if (isDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }

      onOpenChange(false);
    } catch (error) {
      console.error('Error saving config:', error);
      setError('Kunne ikke lagre innstillinger. Prøv igjen senere.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-background">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            <Settings2 className="h-6 w-6 text-blue-600" />
            Innstillinger
          </DialogTitle>
          <DialogDescription className="text-gray-600 dark:text-neutral-400">
            Juster applikasjonsinnstillingene dine her
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <div className="space-y-6 py-4">
          <Card className="border border-blue-100 dark:border-neutral-700">
            <CardContent className="pt-6">
              <div className="space-y-6">
                {/* Transcription Service Selection */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                      Transkripsjonstjeneste
                    </Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <InfoIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">
                            Velg mellom Azure Speech Service og OpenAI Whisper for transkribering av tale til tekst
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  
                  {/* Service selection using buttons instead of RadioGroup */}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={transcriptionService === 'azure' ? 'default' : 'outline'}
                      onClick={() => handleTranscriptionServiceChange('azure')}
                      className={cn(
                        transcriptionService === 'azure' 
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                          : 'bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800',
                        'flex-1'
                      )}
                    >
                      <Mic className="h-4 w-4 mr-2" />
                      Azure Speech
                    </Button>
                    <Button
                      type="button"
                      variant={transcriptionService === 'whisper' ? 'default' : 'outline'}
                      onClick={() => handleTranscriptionServiceChange('whisper')}
                      className={cn(
                        transcriptionService === 'whisper' 
                          ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white'
                          : 'bg-transparent hover:bg-gray-100 dark:hover:bg-gray-800',
                        'flex-1'
                      )}
                    >
                      <Mic className="h-4 w-4 mr-2" />
                      OpenAI Whisper
                    </Button>
                  </div>
                  
                  {/* Whisper Settings (only shown when Whisper is selected) */}
                  {transcriptionService === 'whisper' && (
                    <div className="mt-4 p-4 bg-gray-50 dark:bg-neutral-800 rounded-lg space-y-4">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Whisper-innstillinger
                      </h4>
                      
                      {/* Whisper Model Selection */}
                      <div className="space-y-2">
                        <Label htmlFor="whisper-model" className="text-sm">
                          Modell
                        </Label>
                        <Select value={whisperModel} onValueChange={setWhisperModel}>
                          <SelectTrigger id="whisper-model" className="w-full">
                            <SelectValue placeholder="Velg Whisper-modell" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="whisper-1">whisper-1</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Post-processing Toggle */}
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <Label htmlFor="post-processing" className="text-sm">
                            GPT-4o etterbehandling
                          </Label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Forbedrer transkripsjonen med tegnsetting og formatering
                          </p>
                        </div>
                        <Switch
                          id="post-processing"
                          checked={postProcessing}
                          onCheckedChange={setPostProcessing}
                          className="data-[state=checked]:bg-blue-600"
                        />
                      </div>
                      
                      {/* Temperature Selection (only if post-processing is enabled) */}
                      {postProcessing && (
                        <div className="space-y-2">
                          <Label htmlFor="temperature" className="text-sm">
                            Temperatur for GPT-4o (0.0 - 1.0)
                          </Label>
                          <Select 
                            value={temperature.toString()} 
                            onValueChange={(val) => setTemperature(parseFloat(val))}
                          >
                            <SelectTrigger id="temperature" className="w-full">
                              <SelectValue placeholder="Velg temperatur" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0.0 (mest presis)</SelectItem>
                              <SelectItem value="0.3">0.3</SelectItem>
                              <SelectItem value="0.7">0.7</SelectItem>
                              <SelectItem value="1">1.0 (mest kreativ)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Auto-sletting */}
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                      Auto-sletting
                    </Label>
                    <p className="text-sm text-gray-600 dark:text-neutral-400">
                      Slett konsultasjoner etter 24 timer
                    </p>
                  </div>
                  <Switch
                    checked={autoDelete}
                    onCheckedChange={setAutoDelete}
                    className="data-[state=checked]:bg-blue-600"
                  />
                </div>

                {/* Dark Mode */}
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                      Dark Mode
                    </Label>
                    <p className="text-sm text-gray-600 dark:text-neutral-400">
                      Aktiver dark mode i applikasjonen
                    </p>
                  </div>
                  <Switch
                    checked={isDarkMode}
                    onCheckedChange={handleDarkModeChange}
                    className="data-[state=checked]:bg-blue-600"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button 
              onClick={handleSave} 
              disabled={isSaving}
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Lagrer...' : 'Lagre innstillinger'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsDialog;